// src/features/dashboard/components/EmployeeIdleTime/TeamIdleComparisonChart.tsx

import { useEffect, useRef, useState } from 'react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Label,
} from 'recharts';

import { IdleVsTeamResponse } from '../../types/Employee/employeeIdleTime.types';

interface Props {
  data?: IdleVsTeamResponse;
  loading?: boolean;
}

const EMPLOYEE_COLOR = '#FF7A1A';
const TEAM_COLOR = '#98A2B3';
const GRID_COLOR = '#ECECEC';
const THRESHOLD_COLOR = '#EF4444';

const formatPercent = (value: number) => `${value}%`;

/* -------------------------------------------------------------------------- */
/*                              DATE FORMATTER                                 */
/* -------------------------------------------------------------------------- */

const formatWorkDate = (workDate?: string) => {
  if (!workDate) {
    return '';
  }

  const date = new Date(workDate);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const day = date.toLocaleDateString('en-US', {
    weekday: 'short',
  });

  const dateNumber = date.getDate();

  return `${day} ${dateNumber}`;
};

/* -------------------------------------------------------------------------- */
/*                         CUSTOM X-AXIS TICK                                 */
/* -------------------------------------------------------------------------- */

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value?: string | number;
    index?: number;
  };
  activeIndex?: number | null;
}

const CustomXAxisTick = ({
  x = 0,
  y = 0,
  payload,
  activeIndex,
}: CustomXAxisTickProps) => {
  if (!payload) {
    return null;
  }

  const value =
    payload.value ?? '';

  const index =
    payload.index ?? -1;

  const isActive =
    activeIndex !== null &&
    activeIndex !== undefined &&
    index === activeIndex;

  return (
    <g
      transform={`translate(${x},${y})`}
      pointerEvents="none"
      style={{
        overflow: 'visible',
      }}
    >
      {isActive ? (
        <>
          {/* ==========================================================
              HOVER POINTER

              The pointer is centered exactly over the
              selected X-axis position.
          ========================================================== */}

          <path
            d="
              M -7 6
              L 0 -1
              L 7 6
              Z
            "
            fill="#FFFFFF"
            stroke="#98A2B3"
            strokeWidth={1}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />

          {/* Hide the lower pointer edge where it
              connects to the box. */}
          <rect
            x="-8"
            y="4"
            width="16"
            height="5"
            fill="#FFFFFF"
          />

          {/* ==========================================================
              SELECTED DAY BOX

              Compact white box matching the supplied
              reference design.
          ========================================================== */}

          <rect
            x="-23"
            y="6"
            width="46"
            height="38"
            rx="2"
            fill="#FFFFFF"
            stroke="#98A2B3"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />

          <text
            x="0"
            y="27"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#344054"
            fontSize={10}
            fontWeight={400}
          >
            {String(value)
              .substring(0, 3)
              .toUpperCase()}
          </text>
        </>
      ) : (
        <text
          x="0"
          y="20"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#344054"
          fontSize={11}
          fontWeight={400}
        >
          {value}
        </text>
      )}
    </g>
  );
};

/* -------------------------------------------------------------------------- */
/*                                TOOLTIP                                     */
/* -------------------------------------------------------------------------- */

const CustomTooltip = ({
  active,
  payload,
}: any) => {
  if (!active || !payload?.length) {
    return null;
  }

  const me = payload.find(
    (item: any) =>
      item.dataKey === 'myIdlePercentage',
  );

  const team = payload.find(
    (item: any) =>
      item.dataKey === 'teamAvgIdlePercentage',
  );

  const currentData =
    me?.payload ?? team?.payload;

  return (
    <div
      className="
        rounded-md
        border
        border-[#E4E7EC]
        bg-white
        px-3
        py-2
        shadow-lg
      "
    >
      <p className="mb-1 text-[11px] font-semibold text-[#101828]">
        {currentData?.displayDay ||
          currentData?.dayName ||
          '-'}
      </p>

      <div className="space-y-[2px]">
        <div className="flex items-center justify-between gap-8">
          <span className="text-[11px] text-[#667085]">
            Me
          </span>

          <span className="text-[11px] font-semibold text-[#FF7A1A]">
            {me?.value ?? 0}%
          </span>
        </div>

        <div className="flex items-center justify-between gap-8">
          <span className="text-[11px] text-[#667085]">
            Team Avg
          </span>

          <span className="text-[11px] font-semibold text-[#98A2B3]">
            {team?.value ?? 0}%
          </span>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                              TARGET LABEL                                  */
/* -------------------------------------------------------------------------- */

const TargetLabel = (props: any) => {
  const { viewBox, value } = props;

  if (!viewBox) {
    return null;
  }

  const label = `${value}% target`;
  const boxWidth = label.length * 6 + 14;

  return (
    <g>
      <rect
        x={
          viewBox.x +
          viewBox.width -
          boxWidth / 2
        }
        y={viewBox.y - 10}
        width={boxWidth}
        height={20}
        rx={4}
        fill="#FFF5F5"
        stroke="#FCA5A5"
      />

      <text
        x={viewBox.x + viewBox.width}
        y={viewBox.y + 4}
        textAnchor="middle"
        fontSize={10}
        fill="#DC2626"
      >
        {label}
      </text>
    </g>
  );
};

/* -------------------------------------------------------------------------- */
/*                          MAIN COMPONENT                                    */
/* -------------------------------------------------------------------------- */

const TeamIdleComparisonChart = ({
  data,
  loading,
}: Props) => {
  const chartData = data?.trend ?? [];

  const threshold = Number(
    data?.idlePercentageThreshold ?? 0,
  );

  /* ------------------------------------------------------------------------ */
  /*                         CHART SCROLL REF                                 */
  /* ------------------------------------------------------------------------ */

  const chartScrollRef =
    useRef<HTMLDivElement | null>(null);

  /* ------------------------------------------------------------------------ */
  /*                         VISIBLE DATA                                     */
  /* ------------------------------------------------------------------------ */

  const [visibleCount, setVisibleCount] =
    useState(chartData.length || 5);

  const [hoveredSeries, setHoveredSeries] =
    useState<
      | 'myIdlePercentage'
      | 'teamAvgIdlePercentage'
      | null
    >(null);

  /*
   * Active X-axis point used only for the
   * selected-day hover box.
   */
  const [
    activeXAxisIndex,
    setActiveXAxisIndex,
  ] = useState<number | null>(null);

  /*
   * Prepare chart data.
   *
   * IMPORTANT:
   *
   * API:
   *
   * workDate: 2026-08-17
   * dayName: Monday
   *
   * Chart:
   *
   * Mon 17
   * Tue 18
   * Wed 19
   * Thu 20
   * Fri 21
   */

  const formattedChartData = chartData.map(
    (item: any) => ({
      ...item,

      displayDay:
        formatWorkDate(item.workDate) ||
        item.dayName ||
        '',
    }),
  );

  /*
   * Keep visible count valid when API data changes.
   */
  useEffect(() => {
    setVisibleCount(
      formattedChartData.length || 5,
    );

    setActiveXAxisIndex(null);
  }, [formattedChartData.length]);

  /* ------------------------------------------------------------------------ */
  /*                    CHART-ONLY MOUSE WHEEL                               */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const chartElement =
      chartScrollRef.current;

    if (!chartElement) {
      return;
    }

    const handleWheel = (
      event: WheelEvent,
    ) => {
      /*
       * Stop browser page scrolling.
       */
      event.preventDefault();
      event.stopPropagation();

      if (!formattedChartData.length) {
        return;
      }

      /*
       * Wheel UP
       * Zoom IN
       */
      if (event.deltaY < 0) {
        setVisibleCount((previous) =>
          Math.max(
            2,
            previous - 1,
          ),
        );

        return;
      }

      /*
       * Wheel DOWN
       * Zoom OUT
       */
      if (event.deltaY > 0) {
        setVisibleCount((previous) =>
          Math.min(
            formattedChartData.length,
            previous + 1,
          ),
        );
      }
    };

    /*
     * passive:false is required for preventDefault().
     */
    chartElement.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
      },
    );

    return () => {
      chartElement.removeEventListener(
        'wheel',
        handleWheel,
      );
    };
  }, [formattedChartData.length]);

  /* ------------------------------------------------------------------------ */
  /*                         VISIBLE DATA                                     */
  /* ------------------------------------------------------------------------ */

  const visibleData =
    formattedChartData.length > 0
      ? formattedChartData.slice(
          -visibleCount,
        )
      : [];

  /* ------------------------------------------------------------------------ */
  /*                           Y AXIS                                         */
  /* ------------------------------------------------------------------------ */

  const maxValue = Math.max(
    threshold,
    ...formattedChartData.map(
      (item: any) =>
        Math.max(
          Number(
            item.myIdlePercentage,
          ) || 0,
          Number(
            item.teamAvgIdlePercentage,
          ) || 0,
        ),
    ),
  );

  const yAxisMax =
    Math.ceil(maxValue + 5);

  /* ------------------------------------------------------------------------ */
  /*                              LOADING                                     */
  /* ------------------------------------------------------------------------ */

  if (loading) {
    return (
      <div className="h-[320px] animate-pulse rounded-[18px] bg-gray-200" />
    );
  }

  /* ------------------------------------------------------------------------ */
  /*                              UI                                          */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      className="
        rounded-[18px]
        border
        border-[#D0D5DD]
        bg-white
        px-6
        pt-5
        pb-4
      "
    >
      {/* ------------------------------------------------------------------ */}
      {/* Header                                                             */}
      {/* ------------------------------------------------------------------ */}

      <div className="mb-3 flex items-center gap-3">
        <h3
          className="
            text-[15px]
            font-semibold
            text-[#002B5B]
          "
        >
          Me vs Team · Idle %
        </h3>

        <div
          className="
            rounded-full
            bg-[#EEF4FF]
            px-4
            py-[5px]
            text-[9px]
            text-[#2563EB]
          "
        >
          Daily idle % ·{' '}
          {data?.managerInfo?.managerName ??
            'Team'}
          's Team · This Week
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Legend                                                             */}
      {/* ------------------------------------------------------------------ */}

      <div className="mb-3 flex justify-center gap-5">
        {/* Me */}

        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-1.5
          "
          onMouseEnter={() =>
            setHoveredSeries(
              'myIdlePercentage',
            )
          }
          onMouseLeave={() =>
            setHoveredSeries(null)
          }
        >
          <span
            className="
              h-[14px]
              w-[14px]
              rounded-full
              bg-[#FF7A1A]
            "
          />

          <span
            className={`
              text-[12px]
              ${
                hoveredSeries === null ||
                hoveredSeries ===
                  'myIdlePercentage'
                  ? 'font-semibold text-[#344054]'
                  : 'text-[#98A2B3]'
              }
            `}
          >
            Me (
            {data?.managerInfo
              ?.employeeName ?? '-'}
            )
          </span>
        </div>

        {/* Team */}

        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-1.5
          "
          onMouseEnter={() =>
            setHoveredSeries(
              'teamAvgIdlePercentage',
            )
          }
          onMouseLeave={() =>
            setHoveredSeries(null)
          }
        >
          <span
            className="
              h-[14px]
              w-[14px]
              rounded-full
              bg-[#98A2B3]
            "
          />

          <span
            className={`
              text-[12px]
              ${
                hoveredSeries === null ||
                hoveredSeries ===
                  'teamAvgIdlePercentage'
                  ? 'font-semibold text-[#344054]'
                  : 'text-[#98A2B3]'
              }
            `}
          >
            Team Avg
          </span>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* CHART-ONLY SCROLL AREA                                            */}
      {/* ------------------------------------------------------------------ */}

      <div
        ref={chartScrollRef}
        className="
          h-[250px]
          w-full
          overflow-hidden
          overscroll-contain
          select-none
        "
        style={{
          touchAction: 'none',
        }}
      >
        <ResponsiveContainer
          width="99%"
          height="100%"
        >
          <LineChart
            data={visibleData}
            margin={{
              top: 8,
              right: 39,
              left: 10,
              bottom: 8,
            }}
            onMouseMove={(state: any) => {
              const index =
                state?.activeTooltipIndex;

              if (
                index !== undefined &&
                index !== null &&
                Number.isFinite(
                  Number(index),
                )
              ) {
                setActiveXAxisIndex(
                  Number(index),
                );
              }
            }}
            onMouseLeave={() => {
              setActiveXAxisIndex(null);
            }}
          >
            {/* Grid */}

            <CartesianGrid
              vertical={false}
              stroke={GRID_COLOR}
            />

            {/* X Axis */}

            <XAxis
              dataKey="displayDay"
              interval={0}
              height={58}
              tickMargin={0}
              tick={
                <CustomXAxisTick
                  activeIndex={
                    activeXAxisIndex
                  }
                />
              }
              axisLine={{
                stroke: '#D0D5DD',
              }}
              tickLine={false}
            />

            {/* Y Axis */}

            <YAxis
              domain={[0, yAxisMax]}
              tickCount={6}
              tickFormatter={formatPercent}
              tick={{
                fontSize: 11,
                fill: '#344054',
              }}
              axisLine={false}
              tickLine={false}
              width={50}
            />

            {/* Tooltip */}

            <Tooltip
              cursor={{
                stroke: '#D0D5DD',
                strokeWidth: 1,
                strokeDasharray: '4 4',
              }}
              content={
                <CustomTooltip />
              }
            />

            {/* Target */}

            <ReferenceLine
              y={threshold}
              stroke={THRESHOLD_COLOR}
              strokeWidth={1}
              strokeDasharray="6 6"
            >
              <Label
                position="right"
                value={threshold}
                content={
                  <TargetLabel />
                }
              />
            </ReferenceLine>

            {/* ---------------------------------------------------------- */}
            {/* Employee Line                                               */}
            {/* ---------------------------------------------------------- */}

            <Line
              type="monotone"
              dataKey="myIdlePercentage"
              stroke={EMPLOYEE_COLOR}
              strokeWidth={
                hoveredSeries ===
                'myIdlePercentage'
                  ? 4
                  : 3
              }
              strokeOpacity={
                hoveredSeries === null
                  ? 1
                  : hoveredSeries ===
                    'myIdlePercentage'
                  ? 1
                  : 0.25
              }
              dot={{
                r: 4,
                fill: EMPLOYEE_COLOR,
                fillOpacity:
                  hoveredSeries === null ||
                  hoveredSeries ===
                    'myIdlePercentage'
                    ? 1
                    : 0.25,
              }}
              activeDot={{
                r: 6,
                fill: EMPLOYEE_COLOR,
              }}
              isAnimationActive={false}
            />

            {/* ---------------------------------------------------------- */}
            {/* Team Line                                                   */}
            {/* ---------------------------------------------------------- */}

            <Line
              type="monotone"
              dataKey="teamAvgIdlePercentage"
              stroke={TEAM_COLOR}
              strokeWidth={
                hoveredSeries ===
                'teamAvgIdlePercentage'
                  ? 3.5
                  : 2.5
              }
              strokeOpacity={
                hoveredSeries === null
                  ? 1
                  : hoveredSeries ===
                    'teamAvgIdlePercentage'
                  ? 1
                  : 0.25
              }
              dot={{
                r: 3,
                fill: TEAM_COLOR,
                fillOpacity:
                  hoveredSeries === null ||
                  hoveredSeries ===
                    'teamAvgIdlePercentage'
                    ? 1
                    : 0.25,
              }}
              activeDot={{
                r: 6,
                fill: TEAM_COLOR,
              }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TeamIdleComparisonChart;